import { isConnectedServicesUiEnabled } from "../../connectedServices/connectedServicesGate";
import { ASHLEY_HOME_CATEGORY_ID } from "./ashleyHomeCatalog";

/**
 * Normalize search input for Ashley Home discovery filtering.
 * @param {string} value
 */
export function normalizeAshleyHomeSearch(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s']/g, " ")
    .replace(/\s+/g, " ");
}

/**
 * @param {import("./ashleyHomeCatalog").AshleyHomeCapability} capability
 * @param {string} query
 */
function capabilityMatchesQuery(capability, query) {
  const haystack = [
    capability.title,
    capability.description,
    ...(capability.examplePhrases || []),
    ...(capability.searchTerms || []),
    ...(capability.intentIds || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
}

/**
 * Instant discovery filter over categories, capabilities, and example phrases.
 * Does not execute actions — presentation only.
 *
 * @param {import("./ashleyHomeCatalog").AshleyHomeCategory[]} catalog
 * @param {string} searchText
 * @returns {import("./ashleyHomeCatalog").AshleyHomeCategory[]}
 */
export function filterAshleyHomeCatalog(catalog, searchText) {
  const query = normalizeAshleyHomeSearch(searchText);
  if (!query) {
    return catalog;
  }

  return catalog
    .map((category) => {
      const categoryHaystack = [
        category.title,
        ...(category.searchTerms || []),
      ]
        .join(" ")
        .toLowerCase();

      if (categoryHaystack.includes(query)) {
        return category;
      }

      const matchedCapabilities = (category.capabilities || []).filter((capability) =>
        capabilityMatchesQuery(capability, query),
      );

      if (matchedCapabilities.length === 0) {
        return null;
      }

      return {
        ...category,
        capabilities: matchedCapabilities,
      };
    })
    .filter(Boolean);
}

/**
 * Drop categories whose backing feature is gated off in this environment.
 * Email (Gmail) is only listed when Connected Services are enabled.
 *
 * @param {import("./ashleyHomeCatalog").AshleyHomeCategory[]} catalog
 * @param {Record<string, string | undefined>} [env]
 */
export function excludeGatedAshleyHomeCategories(catalog, env = process.env) {
  if (isConnectedServicesUiEnabled(env)) {
    return catalog;
  }
  return catalog.filter((category) => category.id !== ASHLEY_HOME_CATEGORY_ID.EMAIL);
}
